import { type DirectiveBinding } from 'vue';

import {
    calcCoords,
    getViewportLimits,
    resolvePlacement,
    resolveViewportMaxWidth,
} from './_internal/teleport-positioning';
import type { TooltipPlacement } from './teleport-types';

export interface TooltipBinding {
    toggle?: boolean;
    hover?: boolean;
    focus?: boolean;
    ref?: HTMLElement | { $el: HTMLElement } | null;
    offset?: number;
    placement?: TooltipPlacement;
    width?: string;
    maxWidth?: string;
    /** Широкие попапы (datepicker): ограничить размер и позицию viewport. Тултипы — false. */
    containInViewport?: boolean;
    onChange?: (visible: boolean) => void;
    removeOnUnbind?: boolean;
}

interface TooltipState {
    container: HTMLElement;
    placeholder: Comment;
    value: TooltipBinding;
    hoverRef: boolean;
    hoverTooltip: boolean;
    focused: boolean;
    clickingContainer: boolean;
    leaveTimer: ReturnType<typeof setTimeout> | null;
    rafId: number | null;
    currentRef: HTMLElement | null;
    onRefEnter: () => void;
    onRefLeave: () => void;
    onRefKeyDown: (e: KeyboardEvent) => void;
    onTooltipEnter: () => void;
    onTooltipLeave: () => void;
    onTooltipKeyDown: (e: KeyboardEvent) => void;
    onTooltipFocusOut: (e: FocusEvent) => void;
    onFocusIn: () => void;
    onFocusOut: (e: FocusEvent) => void;
    onDocumentMouseDown: (e: MouseEvent) => void;
    resizeHandler: () => void;
    scrollHandler: () => void;
}

function resolveEl(ref: TooltipBinding['ref']): HTMLElement | null {
    if (!ref) return null;
    if (ref instanceof HTMLElement) return ref;
    if (ref.$el instanceof HTMLElement) return ref.$el;
    return null;
}

const TOOLTIP_KEY = '__v_tooltip__';
const SHARED_CONTAINER_CLASS = 'ui-tooltips__container';

let zCounter = 0;

function applyDefaultContainerSize(
    container: HTMLElement,
    value: TooltipBinding,
    rect: DOMRect,
): void {
    container.style.width = value.width ?? '100%';
    container.style.maxWidth = value.maxWidth === '100%'
        ? `${rect.width}px`
        : (value.maxWidth ?? 'max-content');
    container.style.maxHeight = '';
    container.style.overflow = '';
}

function applyViewportContainerLimits(
    container: HTMLElement,
    value: TooltipBinding,
    rect: DOMRect,
): void {
    const { maxWidth: viewportMaxW, maxHeight: viewportMaxH } = getViewportLimits();
    const resolvedMaxW = resolveViewportMaxWidth(value.maxWidth, rect.width, viewportMaxW);

    container.style.width = value.width ?? 'max-content';
    container.style.maxWidth = `${resolvedMaxW}px`;
    container.style.maxHeight = `${viewportMaxH}px`;
    container.style.overflowY = 'auto';
    container.style.overflowX = 'auto';
}

function getBaseZ(): number {
    return parseInt(getComputedStyle(document.documentElement).getPropertyValue('--ui-z-index-block-everything')) || 10;
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex=\'-1\'])';

function getSharedContainer(): HTMLElement {
    let shared = document.querySelector<HTMLElement>(`.${SHARED_CONTAINER_CLASS}`);
    if (!shared) {
        shared = document.createElement('div');
        shared.classList.add(SHARED_CONTAINER_CLASS);
        document.body.appendChild(shared);
    }
    return shared;
}

function getState(el: HTMLElement): TooltipState | undefined {
    return (el as any)[TOOLTIP_KEY];
}

function getFocusable(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
}

function isVisible(state: TooltipState): boolean {
    const { value, hoverRef, hoverTooltip, focused } = state;
    if (value.toggle === true) return true;
    if (value.toggle === false) return false;
    const hovering = value.hover ? (hoverRef || hoverTooltip) : false;
    const focusing = value.focus ? focused : false;
    return hovering || focusing;
}

function measure(container: HTMLElement): { w: number; h: number } {
    const wasHidden = container.style.display === 'none';
    if (wasHidden) {
        container.style.visibility = 'hidden';
        container.style.display = '';
    }
    const w = container.offsetWidth;
    const h = container.offsetHeight;
    if (wasHidden) {
        container.style.display = 'none';
        container.style.visibility = '';
    }
    return { w, h };
}

function updatePosition(state: TooltipState) {
    const { container, value } = state;
    const refEl = resolveEl(value.ref);
    if (!refEl) return;

    const offset = value.offset ?? 8;
    const preferred: TooltipPlacement = value.placement ?? 'auto';
    const rect = refEl.getBoundingClientRect();

    if (value.containInViewport) {
        applyViewportContainerLimits(container, value, rect);
    } else {
        applyDefaultContainerSize(container, value, rect);
    }

    const { w: cw, h: ch } = measure(container);

    if (value.containInViewport && container.scrollHeight <= container.clientHeight) {
        container.style.overflowY = 'visible';
    }
    if (value.containInViewport && container.scrollWidth <= container.clientWidth) {
        container.style.overflowX = 'visible';
    }
    const placement = resolvePlacement(preferred, rect, cw, ch, offset, Boolean(value.containInViewport));
    const { top, left } = calcCoords(placement, rect, cw, ch, offset);

    const refCx = rect.left + rect.width / 2;
    const refCy = rect.top + rect.height / 2;
    const arrowX = Math.max(8, Math.min(cw - 8, refCx - left));
    const arrowY = Math.max(8, Math.min(ch - 8, refCy - top));

    container.dataset.placement = placement;
    container.style.top = `${top}px`;
    container.style.left = `${left}px`;
    container.style.setProperty('--arrow-x', `${arrowX}px`);
    container.style.setProperty('--arrow-y', `${arrowY}px`);
}

function applyVisibility(state: TooltipState) {
    const visible = isVisible(state);
    const wasHidden = state.container.style.display === 'none';
    if (visible) {
        if (wasHidden) state.container.style.zIndex = String(getBaseZ() + ++zCounter);
        updatePosition(state);
        state.container.style.display = '';
    } else {
        state.container.style.display = 'none';
    }
    state.value.onChange?.(visible);
}

function closeAndReturnFocus(state: TooltipState) {
    state.focused = false;
    state.hoverRef = false;
    state.hoverTooltip = false;
    applyVisibility(state);
    state.currentRef?.focus();
}

function scheduleHide(state: TooltipState) {
    if (state.leaveTimer) clearTimeout(state.leaveTimer);
    state.leaveTimer = setTimeout(() => {
        state.leaveTimer = null;
        applyVisibility(state);
    }, 100);
}

function cancelHide(state: TooltipState) {
    if (state.leaveTimer) {
        clearTimeout(state.leaveTimer);
        state.leaveTimer = null;
    }
}

function attachRefListeners(state: TooltipState, refEl: HTMLElement) {
    refEl.addEventListener('mouseenter', state.onRefEnter);
    refEl.addEventListener('mouseleave', state.onRefLeave);
    refEl.addEventListener('keydown', state.onRefKeyDown);
    refEl.addEventListener('focusin', state.onFocusIn);
    refEl.addEventListener('focusout', state.onFocusOut);
    state.currentRef = refEl;
}

function detachRefListeners(state: TooltipState) {
    if (!state.currentRef) return;
    state.currentRef.removeEventListener('mouseenter', state.onRefEnter);
    state.currentRef.removeEventListener('mouseleave', state.onRefLeave);
    state.currentRef.removeEventListener('keydown', state.onRefKeyDown);
    state.currentRef.removeEventListener('focusin', state.onFocusIn);
    state.currentRef.removeEventListener('focusout', state.onFocusOut);
    state.currentRef = null;
}

export const Teleport = {
    inserted(el: HTMLElement, binding: DirectiveBinding<TooltipBinding>) {
        const value = binding.value ?? {};

        const placeholder = document.createComment('v-teleport');
        el.parentNode!.insertBefore(placeholder, el);

        const container = document.createElement('div');
        container.style.cssText = 'position:fixed;z-index:var(--ui-z-index-block-everything);box-sizing:border-box;';

        container.appendChild(el);
        getSharedContainer().appendChild(container);

        const state: TooltipState = {
            container,
            placeholder,
            value,
            hoverRef: false,
            hoverTooltip: false,
            focused: false,
            leaveTimer: null,
            rafId: null,
            clickingContainer: false,
            currentRef: null,
            onRefEnter() {
                cancelHide(state);
                state.hoverRef = true;
                applyVisibility(state);
            },
            onRefLeave() {
                state.hoverRef = false;
                scheduleHide(state);
            },
            onRefKeyDown(e: KeyboardEvent) {
                if (!isVisible(state)) return;
                if (e.key === 'Escape') {
                    closeAndReturnFocus(state);
                    return;
                }
                if (e.key === 'Tab' && !e.shiftKey) {
                    const focusable = getFocusable(state.container);
                    if (focusable.length) {
                        e.preventDefault();
                        focusable[0].focus();
                    }
                }
            },
            onTooltipEnter() {
                cancelHide(state);
                state.hoverTooltip = true;
                applyVisibility(state);
            },
            onTooltipLeave() {
                state.hoverTooltip = false;
                scheduleHide(state);
            },
            onTooltipKeyDown(e: KeyboardEvent) {
                if (e.key === 'Escape') {
                    closeAndReturnFocus(state);
                    return;
                }
                if (e.key === 'Tab') {
                    const focusable = getFocusable(state.container);
                    const first = focusable[0];
                    const last = focusable[focusable.length - 1];
                    if (e.shiftKey && document.activeElement === first) {
                        e.preventDefault();
                        state.currentRef?.focus();
                    } else if (!e.shiftKey && document.activeElement === last) {
                        e.preventDefault();
                        state.currentRef?.focus();
                    }
                }
            },
            onTooltipFocusOut(e: FocusEvent) {
                if (state.value.hover) return;
                const related = e.relatedTarget as Node | null;
                if (state.container.contains(related)) return;
                if (state.currentRef === related || state.currentRef?.contains(related)) return;
                if (state.clickingContainer) return;
                state.focused = false;
                applyVisibility(state);
            },
            onFocusIn() {
                if (state.value.hover) return;
                state.focused = true;
                applyVisibility(state);
            },
            onFocusOut(e: FocusEvent) {
                if (state.value.hover) return;
                if (state.container.contains(e.relatedTarget as Node)) return;
                if (state.clickingContainer) return;
                state.focused = false;
                applyVisibility(state);
            },
            onDocumentMouseDown(e: MouseEvent) {
                if (state.value.hover) return;
                const target = e.target as Node;
                if (state.container.contains(target)) return;
                if (state.currentRef === target || state.currentRef?.contains(target)) return;
                state.focused = false;
                applyVisibility(state);
            },
            resizeHandler() {
                if (state.container.style.display === 'none') return;
                if (state.rafId) cancelAnimationFrame(state.rafId);
                state.rafId = requestAnimationFrame(() => {
                    state.rafId = null;
                    updatePosition(state);
                });
            },
            scrollHandler() {
                if (state.container.style.display === 'none') return;
                if (state.rafId) cancelAnimationFrame(state.rafId);
                state.rafId = requestAnimationFrame(() => {
                    state.rafId = null;
                    updatePosition(state);
                });
            },
        };

        (el as any)[TOOLTIP_KEY] = state;

        container.addEventListener('mousedown', () => {
            state.clickingContainer = true;
            setTimeout(() => { state.clickingContainer = false; }, 0);
        });
        container.addEventListener('mouseenter', state.onTooltipEnter);
        container.addEventListener('mouseleave', state.onTooltipLeave);
        container.addEventListener('keydown', state.onTooltipKeyDown);
        container.addEventListener('focusout', state.onTooltipFocusOut);
        window.addEventListener('resize', state.resizeHandler);
        window.addEventListener('scroll', state.scrollHandler, true);
        document.addEventListener('mousedown', state.onDocumentMouseDown, true);

        const refEl = resolveEl(value.ref);
        if (refEl) attachRefListeners(state, refEl);

        applyVisibility(state);
    },

    update(el: HTMLElement, binding: DirectiveBinding<TooltipBinding>) {
        const state = getState(el);
        if (!state) return;

        const value = binding.value ?? {};
        const resolvedRef = resolveEl(value.ref);
        state.value = value;

        if (resolvedRef !== state.currentRef) {
            detachRefListeners(state);
            if (resolvedRef) attachRefListeners(state, resolvedRef);
        }

        if (!value.hover) state.hoverRef = false;
        if (!value.focus) state.focused = false;

        applyVisibility(state);
    },

    unbind(el: HTMLElement) {
        const state = getState(el);
        if (!state) return;

        if (state.leaveTimer) clearTimeout(state.leaveTimer);
        if (state.rafId) cancelAnimationFrame(state.rafId);

        detachRefListeners(state);
        state.container.removeEventListener('mouseenter', state.onTooltipEnter);
        state.container.removeEventListener('mouseleave', state.onTooltipLeave);
        state.container.removeEventListener('keydown', state.onTooltipKeyDown);
        state.container.removeEventListener('focusout', state.onTooltipFocusOut);
        window.removeEventListener('resize', state.resizeHandler);
        window.removeEventListener('scroll', state.scrollHandler, true);
        document.removeEventListener('mousedown', state.onDocumentMouseDown, true);

        if (!state.value.removeOnUnbind && state.placeholder.parentNode) {
            state.placeholder.parentNode.insertBefore(el, state.placeholder);
        }

        state.placeholder.remove();
        state.container.remove();
        delete (el as any)[TOOLTIP_KEY];
    },
};
